/**
 * AutoCount - Part Detail Page
 * Loads a part by route id, plus the warehouse stock position for it.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { getPart, listInventory, Part, InventoryItem, ApiError } from "api";

const formatPrice = (value: number | null): string =>
  value === null ? "—" : `$${value.toFixed(2)}`;

const formatEnum = (value: string): string =>
  value
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

function Field({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <>
      <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
        {label}
      </MDTypography>
      <MDTypography variant="body2">{value}</MDTypography>
    </>
  );
}

function PartsDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [part, setPart] = useState<Part | null>(null);
  const [stock, setStock] = useState<InventoryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getPart(id)
      .then(async (loadedPart) => {
        if (cancelled) return;
        setPart(loadedPart);
        // Stock position in the user's warehouse (may not exist yet)
        try {
          const inventory = await listInventory({ partId: loadedPart.id });
          if (!cancelled) setStock(inventory.items[0] || null);
        } catch {
          if (!cancelled) setStock(null);
        }
        if (!cancelled) setLoading(false);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" py={12}>
          <CircularProgress color="info" />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error || !part) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Alert severity="error">{error || "Part not found"}</Alert>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const onHand = stock?.quantityOnHand ?? 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Part Information Card */}
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Part Details
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <MDTypography variant="h4" fontWeight="medium">
                  {part.name}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Part #: {part.partNumber}
                  {part.oemPartNumber && ` | OEM #: ${part.oemPartNumber}`}
                </MDTypography>

                {part.imageUrl && (
                  <MDBox mt={2}>
                    <img
                      src={part.imageUrl}
                      alt={part.name}
                      style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8 }}
                    />
                  </MDBox>
                )}

                {part.description && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="body2" color="text">
                      {part.description}
                    </MDTypography>
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Field
                      label="Category"
                      value={
                        part.subCategory
                          ? `${part.subCategory.category ? formatEnum(part.subCategory.category.name) + " / " : ""}${part.subCategory.name}`
                          : "—"
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field label="Brand" value={part.brand?.name || "—"} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field label="Type" value={formatEnum(part.partType)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field label="Condition" value={formatEnum(part.condition)} />
                  </Grid>
                  {part.manufacturer && (
                    <Grid item xs={6} md={3}>
                      <Field label="Manufacturer" value={part.manufacturer.name} />
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <MDTypography variant="h6" fontWeight="medium" mb={1}>
                  Pricing
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Cost
                    </MDTypography>
                    <MDTypography variant="h5" color="success">
                      {formatPrice(part.costPrice)}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={4}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Wholesale
                    </MDTypography>
                    <MDTypography variant="h5">
                      {formatPrice(part.wholesalePrice)}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={4}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Retail
                    </MDTypography>
                    <MDTypography variant="h5" color="info">
                      {formatPrice(part.retailPrice)}
                    </MDTypography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <MDTypography variant="h6" fontWeight="medium" mb={1}>
                  Specifications
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Field
                      label="Weight"
                      value={part.weight ? `${part.weight} ${part.weightUnit || "lbs"}` : "—"}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      label="Warranty"
                      value={part.warrantyMonths ? `${part.warrantyMonths} months` : "—"}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field label="Min Stock" value={part.minStockLevel} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field label="Reorder Point" value={part.reorderPoint} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      label="Unit of Measure"
                      value={part.unitOfMeasure || "each"}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field label="Default Location" value={part.location || "—"} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field label="Supplier" value={part.supplierName || "—"} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      label="Lead Time"
                      value={
                        part.supplierLeadTimeDays != null
                          ? `${part.supplierLeadTimeDays} days`
                          : "—"
                      }
                    />
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Inventory Status Card */}
          <Grid item xs={12} lg={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Inventory Status
                </MDTypography>
                <MDBox textAlign="center" py={2}>
                  <MDTypography
                    variant="h1"
                    color={
                      onHand > part.reorderPoint
                        ? "info"
                        : onHand > 0
                        ? "warning"
                        : "error"
                    }
                  >
                    {onHand}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    Units on Hand
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                {stock ? (
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Field label="Available" value={stock.quantityAvailable} />
                    </Grid>
                    <Grid item xs={6}>
                      <Field label="Reserved" value={stock.quantityReserved} />
                    </Grid>
                    <Grid item xs={6}>
                      <Field label="On Order" value={stock.quantityOnOrder} />
                    </Grid>
                    <Grid item xs={6}>
                      <Field label="Bin Location" value={stock.binLocation || "—"} />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        label="Last Count"
                        value={stock.lastCountQty !== null ? stock.lastCountQty : "—"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        label="Counted On"
                        value={
                          stock.lastCountDate
                            ? new Date(stock.lastCountDate).toLocaleDateString()
                            : "—"
                        }
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <MDTypography variant="body2" color="text">
                    No stock record in your warehouse yet. Scan this part to
                    create one.
                  </MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PartsDetail;
